import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import React, { useCallback } from "react";
import { AlgoliaGlobalSearchIndexNodeHit } from "src/gatsby/algolia/type";
import * as styles from "./StandardPageHit.module.scss";
import {
  ImageRatio,
  isSanityImagePresent,
  Logo,
  ResponsiveSanityImage
} from "@silversea-ssc/ui-components-react";
import { Highlight } from "react-instantsearch";
import type { SendEventForHits } from "instantsearch.js/es/lib/utils";
import { trackAlgoliaResultClick } from "components/Algolia/algoliaAnalytics";

interface StandardPageHitProps {
  hit: AlgoliaGlobalSearchIndexNodeHit;
  algoliaSendEvent: SendEventForHits;
}

const hitTypeCategoryMap = {
  SanityPage: msg`Page`,
  SanityLandingPage: msg`Page`,
  SanityDestination: msg`Destination`,
  SanitySubDestination: msg`Destination`,
  SanityShip: msg`Ship`,
  SanitySuiteCategory: msg`Suite`,
  SanityPressRelease: msg`Press Release`,
  SanityDiningAreaCategory: msg`Dining Area`
};

const hitTypeToPageCategory = (
  t: ReturnType<typeof useLingui>["t"],
  type: string
): string => {
  const mappedLabel = hitTypeCategoryMap[type];
  return mappedLabel ? t(hitTypeCategoryMap[type]) : "";
};

const FallbackImage = () => {
  return (
    <div className={styles.fallbackImageContainer}>
      <ImageRatio ratio="card-compact">
        <Logo color="titanium" />
      </ImageRatio>
    </div>
  );
};

const StandardPageHit: React.FC<
  React.PropsWithChildren<StandardPageHitProps>
> = ({ hit, algoliaSendEvent }) => {
  const { t } = useLingui();
  const { card, metadata } = hit;
  const image = card?.image || metadata?.image;
  const title = card?.title || metadata?.title;
  const descriptionKey = card?.description
    ? "card.description"
    : "metadata.description";

  const onClick = useCallback(
    (_e: React.MouseEvent<HTMLButtonElement>) => {
      trackAlgoliaResultClick(algoliaSendEvent, hit, "Page clicked");
    },
    [algoliaSendEvent, hit]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        onClick(e);
      }
    },
    [onClick]
  );

  return (
    <article
      onClick={onClick}
      onAuxClick={onClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <a
        href={hit.path}
        className={styles.row}
        data-content-piece="page_result"
      >
        <div className={styles.image}>
          {isSanityImagePresent(image) ? (
            <ImageRatio ratio="card-compact">
              <ResponsiveSanityImage
                image={image}
                defaultWidth={200}
                defaultHeight={150}
                alt={title}
                loading="lazy"
              />
            </ImageRatio>
          ) : (
            <FallbackImage />
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.category}>
            {hitTypeToPageCategory(t, hit._type)}
          </div>
          <div className={styles.title}>{title}</div>
          <div className={styles.description}>
            <Highlight hit={hit} attribute={descriptionKey} />
          </div>
        </div>
      </a>
    </article>
  );
};

export default StandardPageHit;
